import { Box, ButtonBase, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import theme from "@/theme";
import { buildAutomationAttributes } from "@/utilities/automation";

import { ThinArrowIcon } from "../icon/ThinArrowIcon";

export const useDetailStyles = makeStyles({
  section: {
    minWidth: 0,
    padding: layout.space[24],
    background: colors["surface/card"],
    [theme.breakpoints.up("md")]: { borderRadius: layout.radius.lg, padding: "20px 24px" },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: layout.space[16],
    marginBottom: layout.space[8],
  },
  title: {
    ...textStyles["SP/Heading/H3"],
    color: colors["text/primary"],
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Heading/H4"] },
  },
  edit: {
    ...textStyles["SP/Button/M"],
    minHeight: 44,
    color: colors["action/primary"],
    gap: layout.space[4],
    flexShrink: 0,
  },
  arrow: { width: 16, height: 16 },
  rows: {
    [theme.breakpoints.up("md")]: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
      columnGap: layout.space[24],
    },
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minWidth: 0,
    gap: layout.space[8],
    borderBottom: `1px solid ${colors["border/default"]}`,
    padding: "12px 0",
  },
  label: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    minWidth: 0,
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Body/Small"] },
  },
  value: {
    ...textStyles["SP/Body/Large"],
    color: colors["text/primary"],
    textAlign: "right",
    minWidth: 0,
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Body/Small"] },
  },
  link: {
    display: "flex",
    width: "100%",
    textAlign: "left",
    alignItems: "center",
    justifyContent: "space-between",
    gap: layout.space[16],
    padding: layout.space[16],
    borderRadius: layout.radius.lg,
    background: colors["surface/card"],
    boxShadow: theme.shadows[1],
    color: colors["text/primary"],
    "&:focus-visible": { outline: `2px solid ${colors["border/focus"]}` },
  },
  linkTitle: { ...textStyles["SP/Button/M"], [theme.breakpoints.up("md")]: { ...textStyles["PC/Heading/H5"] } },
  linkDescription: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    marginTop: layout.space[4],
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Body/Small"] },
  },
});

export const DetailSection: React.FC<{
  title: string;
  onEdit?: () => void;
  editLabel?: string;
  id?: string;
  qaId?: string;
  children: React.ReactNode;
}> = ({ title, onEdit, editLabel = "編集", id, qaId, children }) => {
  const classes = useDetailStyles();
  return (
    <Box component="section" className={classes.section} id={id}>
      <Box className={classes.header}>
        <Typography component="h2" className={classes.title}>
          {title}
        </Typography>
        {onEdit && (
          <ButtonBase
            {...buildAutomationAttributes({ qaId })}
            className={classes.edit}
            onClick={onEdit}
            aria-label={`${title}を${editLabel}`}
          >
            {editLabel}
            <ThinArrowIcon className={classes.arrow} />
          </ButtonBase>
        )}
      </Box>
      {children}
    </Box>
  );
};

export const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  const classes = useDetailStyles();
  return (
    <Box className={classes.row}>
      <Typography className={classes.label}>{label}</Typography>
      <Typography component="div" className={classes.value}>
        {value ?? "-"}
      </Typography>
    </Box>
  );
};

export const DetailLink: React.FC<{ title: string; description?: string; onClick: () => void; id?: string }> = ({
  title,
  description,
  onClick,
  id,
}) => {
  const classes = useDetailStyles();
  return (
    <ButtonBase className={classes.link} onClick={onClick} id={id}>
      <Box>
        <Typography className={classes.linkTitle}>{title}</Typography>
        {description && <Typography className={classes.linkDescription}>{description}</Typography>}
      </Box>
      <ThinArrowIcon className={classes.arrow} />
    </ButtonBase>
  );
};
